import React, { useEffect, useState } from 'react';
import { MetricsCards } from './MetricsCards';
import { ChartsSection } from './ChartsSection';
import { JurisdictionsTable } from './JurisdictionsTable';
import ActivityFeed from './ActivityFeed';
import { UserEngagementMetrics } from './UserEngagementMetrics';
import styles from './Statistics.module.css';
import * as dashboardApi from '../../../../api/dashboardApi';
import * as adminApi from '../../../../api/adminApi';
import { UserEngagementMetricsData, FeedbackStats } from '../../../../api/adminApi';
import { Eyebrow, PageHeading, InlineAlert } from '../../../../design-system';
import { useAuth } from '../../../../context/AuthContext';

export interface StatisticsPageProps {
  token?: string;
  preloadedStats?: adminApi.AdminDashboardStats;
  preloadedTrends?: dashboardApi.TrendDataPoint[];
  preloadedJurisdictionStats?: dashboardApi.JurisdictionStats[];
}

interface Jurisdiction {
  name: string;
  policies: number;
}

export const StatisticsPage: React.FC<StatisticsPageProps> = ({
  token: propToken,
  preloadedStats,
  preloadedTrends,
  preloadedJurisdictionStats,
}) => {
  const auth = useAuth();
  const token = propToken || (auth ? auth.token : undefined) || '';
  
  const [metrics, setMetrics] = useState({
    totalPolicies: 0,
    activeJurisdictions: 0,
    totalUsers: 0,
    totalAdmins: 0,
  });
  const [jurisdictions, setJurisdictions] = useState<Jurisdiction[]>([]);
  const [activities, setActivities] = useState<dashboardApi.ActivityEvent[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [policyTrend, setPolicyTrend] = useState<number | null>(null);
  const [policySparkline, setPolicySparkline] = useState<number[]>([]);
  const [engagementMetrics, setEngagementMetrics] = useState<UserEngagementMetricsData | undefined>(undefined);
  const [feedback, setFeedback] = useState<FeedbackStats | null>(null);
  const [rawTrendsData, setRawTrendsData] = useState<dashboardApi.TrendDataPoint[] | undefined>(undefined);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        setError(null);

        // Use preloaded data from parent when available; only fetch what's missing
        const [
          statsResult,
          totalResult,
          jurisdictionsResult,
          trendsResult,
          jurisdictionStatsResult,
          engagementResult,
          feedbackResult,
        ] = await Promise.allSettled([
          preloadedStats
            ? Promise.resolve(preloadedStats)
            : token
              ? adminApi.getAdminDashboardStats(token)
              : Promise.resolve({ users: { users: 0, admins: 0 }, jurisdictions: { total: 0 } }),
          dashboardApi.getTotalRegulations(),
          dashboardApi.getTotalJurisdictions(),
          preloadedTrends
            ? Promise.resolve({ data: preloadedTrends })
            : dashboardApi.getTrends({ granularity: 'month' }),
          preloadedJurisdictionStats
            ? Promise.resolve({ stats: preloadedJurisdictionStats })
            : dashboardApi.getJurisdictionStats({}),
          token ? adminApi.getUserEngagementMetrics(token) : Promise.resolve(null),
          token ? adminApi.getFeedbackStats(token) : Promise.resolve(null),
        ]);

        // Load activity feed independently so it doesn't block main metrics render
        dashboardApi.getActivityFeed({ days: 2 }).then(res => {
          setActivities((res?.items || []).slice(0, 50) as any);
        }).catch(() => {});

        const statsResponse =
          statsResult.status === 'fulfilled'
            ? statsResult.value
            : { users: { users: 0, admins: 0 }, jurisdictions: { total: 0 } };

        const totalPolicies =
          totalResult.status === 'fulfilled' ? (totalResult.value?.total || 0) : 0;

        const activeJurisdictions =
          jurisdictionsResult.status === 'fulfilled'
            ? (jurisdictionsResult.value?.total ?? statsResponse.jurisdictions.total ?? 0)
            : (statsResponse.jurisdictions.total ?? 0);

        let computedPolicyTrend: number | null = null;
        let computedSparkline: number[] = [];
        if (trendsResult.status === 'fulfilled') {
          const trendData = trendsResult.value?.data || [];
          setRawTrendsData(trendData);
          if (trendData.length > 0) {
            const history = trendData.map((pt: any) => pt.regulations_count || 0);
            computedSparkline = history.slice(Math.max(0, history.length - 8));
          }
          if (trendData.length >= 2) {
            const last = trendData[trendData.length - 1];
            const prev = trendData[trendData.length - 2];
            if (prev.regulations_count > 0) {
              computedPolicyTrend = Math.round(
                ((last.regulations_count - prev.regulations_count) / prev.regulations_count) * 100,
              );
            }
          }
        }

        const jurisdictionsList: Jurisdiction[] =
          jurisdictionStatsResult.status === 'fulfilled'
            ? (jurisdictionStatsResult.value?.stats || []).slice(0, 5).map((stat: any) => ({
                name: stat.jurisdiction_name,
                policies: stat.regulation_count,
              }))
            : [];

        if (engagementResult.status === 'fulfilled' && engagementResult.value) {
          setEngagementMetrics(engagementResult.value);
        }

        if (feedbackResult.status === 'fulfilled' && feedbackResult.value) {
          setFeedback(feedbackResult.value);
        }

        setMetrics({
          totalPolicies,
          activeJurisdictions,
          totalUsers: statsResponse.users.users,
          totalAdmins: statsResponse.users.admins,
        });
        setPolicyTrend(computedPolicyTrend);
        setPolicySparkline(computedSparkline);
        setJurisdictions(jurisdictionsList);
      } catch (err) {
        console.error('Failed to fetch statistics:', err);
        setError('Failed to load statistics. Please try again.');
        setMetrics({ totalPolicies: 0, activeJurisdictions: 0, totalUsers: 0, totalAdmins: 0 });
        setPolicyTrend(null);
        setPolicySparkline([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [token, preloadedStats, preloadedTrends, preloadedJurisdictionStats]);

  if (isLoading) {
    return (
      <div className={styles.container}>
        <div className={styles.centerLoading}>
          <div className={styles.spinner} />
          <p>Loading statistics…</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div style={{ marginBottom: '1.25rem' }}>
        <Eyebrow tone="default" dot>OVERVIEW</Eyebrow>
        <PageHeading
          accentWord="Statistics"
          accentTone="orange"
          size="h2"
          align="left"
          level="h1"
        >
          Statistics Dashboard
        </PageHeading>
      </div>

      {error && (
        <div style={{ marginBottom: '1.5rem' }}>
          <InlineAlert variant="danger" title={error} />
        </div>
      )}

      {/* Country filter removed from statistics page */}

      {/* Top Metrics Section */}
      <MetricsCards 
        metrics={{
          totalPolicies: metrics.totalPolicies,
          activeJurisdictions: metrics.activeJurisdictions,
          totalUsers: metrics.totalUsers,
          totalAdmins: metrics.totalAdmins,
        }} 
        isLoading={isLoading}
        trends={{
          totalPoliciesTrend: policyTrend ?? undefined,
          totalPoliciesHistory: policySparkline.length ? policySparkline : undefined,
        }}
      />

      {/* Charts Section */}
      <ChartsSection isLoading={isLoading} totalPolicies={metrics.totalPolicies} showSkeletons={false} trends={rawTrendsData} />

      {/* Bottom Row - Jurisdictions and Activity Feed */}
      <div className={styles.gridTwoColumn}>
        <JurisdictionsTable isLoading={isLoading} jurisdictions={jurisdictions} />
        <ActivityFeed isLoading={isLoading} activities={activities} />
      </div>

      {/* User Engagement Metrics */}
      <UserEngagementMetrics isLoading={isLoading} data={engagementMetrics} />

      {/* Chat Feedback — 👍/👎 on assistant replies */}
      <div style={{ marginTop: '1.5rem' }}>
        <h3 style={{ fontSize: '0.95rem', fontWeight: 600, margin: '0 0 0.75rem' }}>Chat Feedback</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '0.75rem' }}>
          {[
            { label: 'Helpful 👍', value: String(feedback?.up ?? 0) },
            { label: 'Not helpful 👎', value: String(feedback?.down ?? 0) },
            { label: 'Total rated', value: String(feedback?.total ?? 0) },
            { label: 'Helpful rate', value: `${feedback?.helpful_pct ?? 0}%` },
          ].map(c => (
            <div
              key={c.label}
              style={{
                border: '1px solid var(--ds-border, #e5e7eb)',
                borderRadius: 12,
                padding: '1rem',
                background: 'var(--ds-surface, #fff)',
              }}
            >
              <div style={{ fontSize: '1.5rem', fontWeight: 700, lineHeight: 1.1 }}>{c.value}</div>
              <div style={{ fontSize: '0.8rem', color: 'var(--ds-text-muted, #6b7280)', marginTop: 4 }}>{c.label}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default StatisticsPage;
